import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';

import Iconify from 'src/components/iconify';
import { useAuthContext } from 'src/auth/hooks';
import { useOrganizationContext } from 'src/hooks/use-organization-context';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { Role } from 'src/models/user';

export default function ImpersonationBanner() {
  const theme = useTheme();
  const router = useRouter();
  const { user } = useAuthContext();
  const { currentOrg, organizations, handleOrganizationSwitchAndExit } =
    useOrganizationContext();

  const isSuperAdmin = user?.role === Role.SUPER_ADMIN;

  // Only super admins who have switched into an org are "impersonating"
  if (!isSuperAdmin || !currentOrg) return null;

  const orgName =
    organizations.find((o) => o._id === currentOrg)?.name ?? 'organization';

  const handleExit = () => {
    handleOrganizationSwitchAndExit(null);
    router.push(paths.admin.root);
  };

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: (t) => t.zIndex.appBar + 1,
        bgcolor: alpha(theme.palette.warning.main, 0.95),
        color: theme.palette.warning.contrastText,
        backdropFilter: 'blur(6px)',
        borderBottom: `1px solid ${alpha(theme.palette.warning.dark, 0.5)}`,
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="center"
        spacing={1.5}
        sx={{ py: 1, px: 2 }}
      >
        <Stack direction="row" alignItems="center" spacing={1} justifyContent="center">
          <Iconify icon="solar:shield-warning-bold" width={20} />
          <Typography variant="body2" fontWeight={700}>
            Acting as <Box component="span" sx={{ textDecoration: 'underline' }}>{orgName}</Box>
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            (super-admin impersonation)
          </Typography>
        </Stack>
        <Button
          variant="contained"
          color="inherit"
          size="small"
          onClick={handleExit}
          startIcon={<Iconify icon="solar:logout-3-bold" width={16} />}
          sx={{
            bgcolor: 'common.white',
            color: 'warning.dark',
            fontWeight: 700,
            '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.85) },
          }}
        >
          Exit
        </Button>
      </Stack>
    </Box>
  );
}
